import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { AIToolUiStyleHost } from "../../../components";
import ChatBubble from "./ChatBubble";
import { applyBubbleHtml } from "./chatBubbleDom.js";
import {
  assistantTextBubbleController,
  assistantThinkingBubbleController,
  userMessageBubbleController,
} from "../../../systems/core/controllers";

function createBubbleId() {
  return `bubble-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createToolBubbleConfig(props = {}) {
  return {
    role: "assistant",
    html:
      typeof props.html === "string"
        ? props.html
        : typeof props.toolUiHtml === "string"
          ? props.toolUiHtml
          : "",
    ...props,
    update: (existingBubble, updates = {}) => {
      const nextBubble = { ...existingBubble, ...updates };
      if (!Object.prototype.hasOwnProperty.call(updates, "html")) {
        nextBubble.html =
          typeof nextBubble.toolUiHtml === "string" ? nextBubble.toolUiHtml : "";
      }
      return nextBubble;
    },
    getContent: (bubble) => bubble?.content || "",
  };
}

function createGeneratingBubbleConfig(props = {}) {
  return {
    role: "assistant",
    html: `
      <div class="bubble-controller-generating" data-bubble-context-menu-target="true">
        <span class="bubble-controller-generating-dot"></span>
        <span class="bubble-controller-generating-dot"></span>
        <span class="bubble-controller-generating-dot"></span>
      </div>
    `.trim(),
    ...props,
    getContent: () => "",
  };
}

function buildLegacyBubbleConfig(preset, props = {}) {
  switch (preset) {
    case "assistantText":
      return assistantTextBubbleController.createBubble(props);
    case "assistantThinking":
      return assistantThinkingBubbleController.createBubble(props);
    case "assistantFunctionCall":
      return createToolBubbleConfig(props);
    case "userText":
      return userMessageBubbleController.createBubble({ ...props, kind: "text" });
    case "userImages":
      return userMessageBubbleController.createBubble({ ...props, kind: "images" });
    case "userFiles":
      return userMessageBubbleController.createBubble({ ...props, kind: "files" });
    case "generating":
      return createGeneratingBubbleConfig(props);
    default:
      return assistantTextBubbleController.createBubble(props);
  }
}

const ChatArea = forwardRef((props, ref) => {
  const {
    aiToolSystem = null,
    onImageClick,
    onFileClick,
    onOpenFileByPath,
    onOpenUrl,
    onBubbleClickAction,
    onBubbleContextMenu,
    isMutationBlocked,
    onBubblesChange,
  } = props;
  const [bubbles, setBubbles] = useState([]);
  const bubblesRef = useRef(bubbles);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const isAutoAdjustingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const userScrollCooldownUntilRef = useRef(0);
  const autoScrollEnabledRef = useRef(true);

  useEffect(() => {
    bubblesRef.current = bubbles;
  }, [bubbles]);

  useEffect(() => {
    onBubblesChange?.(bubbles.length);
  }, [bubbles, onBubblesChange]);

  useEffect(() => {
    autoScrollEnabledRef.current = isAutoScrollEnabled;
  }, [isAutoScrollEnabled]);

  const localStreamingManagerRef = useRef({
    elements: new Map(),
    bubbleHtmlByBubbleId: new Map(),
    registerElement: (bubbleId, type, element) => {
      if (!localStreamingManagerRef.current.elements.has(bubbleId)) {
        localStreamingManagerRef.current.elements.set(bubbleId, {});
      }
      const entry = localStreamingManagerRef.current.elements.get(bubbleId);
      entry[type] = element;

      if (type === "rootElement") {
        const cachedHtml =
          localStreamingManagerRef.current.bubbleHtmlByBubbleId.get(bubbleId);
        if (typeof cachedHtml === "string") {
          applyBubbleHtml(element, cachedHtml);
        }
      }
    },
    unregisterElement: (bubbleId, type) => {
      const entry = localStreamingManagerRef.current.elements.get(bubbleId);
      if (!entry) {
        return;
      }
      delete entry[type];
      if (Object.keys(entry).length === 0) {
        localStreamingManagerRef.current.elements.delete(bubbleId);
      }
    },
    updateBubbleHtml: (bubbleId, html, rawContent) => {
      localStreamingManagerRef.current.bubbleHtmlByBubbleId.set(bubbleId, html || "");
      const entry = localStreamingManagerRef.current.elements.get(bubbleId);
      if (entry) {
        if (rawContent !== undefined) {
          entry.rawContent = rawContent;
        }
        entry.currentHtml = html || "";
        if (entry.rootElement) {
          applyBubbleHtml(entry.rootElement, html || "");
        }
      }
    },
    updateToolUiHtml: (bubbleId, html) => {
      localStreamingManagerRef.current.updateBubbleHtml(bubbleId, html);
    },
    updateBubbleDurationText: (bubbleId, text) => {
      const entry = localStreamingManagerRef.current.elements.get(bubbleId);
      if (!entry) {
        return;
      }
      entry.currentDurationDisplay = text;
      const durationElement = entry.rootElement?.querySelector?.(
        "[data-bubble-duration]"
      );
      if (durationElement) {
        durationElement.textContent = text;
      }

      // Keep the chat pinned to the bottom while thinking duration ticks.
      // Duration updates happen frequently; this avoids waiting for a re-render.
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    scrollThinkingBodyToBottom: (bubbleId) => {
      const entry = localStreamingManagerRef.current.elements.get(bubbleId);
      if (!entry?.rootElement) {
        return;
      }

      const thinkingBody = entry.rootElement.querySelector(
        ".bubble-controller-thinking-body"
      );
      if (!thinkingBody) {
        return;
      }

      // Pin internal thinking scroll to bottom as content streams in.
      thinkingBody.scrollTop = thinkingBody.scrollHeight;
    },
  });

  const getIsNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const threshold = 20;
    return (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - threshold
    );
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (!autoScrollEnabledRef.current) {
      return;
    }
    if (Date.now() < userScrollCooldownUntilRef.current) {
      return;
    }
    isAutoAdjustingRef.current = true;
    container.scrollTop = container.scrollHeight;
    requestAnimationFrame(() => {
      isAutoAdjustingRef.current = false;
      setIsNearBottom(true);
    });
  }, []);

  const buildBubbleUiApi = useCallback(
    (bubbleId) => ({
      updateBubbleHtml: (html, rawContent) => {
        localStreamingManagerRef.current.updateBubbleHtml(
          bubbleId,
          html,
          rawContent
        );
        const bubbleEntry = bubblesRef.current.find(
          (b) => b.bubbleId === bubbleId
        );
        const isThinkingBubble = bubbleEntry?.controllerId === "assistant-thinking";

        // Thinking controllers expect to keep the viewport pinned while updating.
        if (isThinkingBubble) {
          const container = scrollContainerRef.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
            setIsNearBottom(true);
          }
          return;
        }

        if (autoScrollEnabledRef.current) {
          requestAnimationFrame(scrollToBottom);
        }
      },
      updateToolUiHtml: (html) => {
        localStreamingManagerRef.current.updateToolUiHtml(bubbleId, html);
        if (autoScrollEnabledRef.current) {
          requestAnimationFrame(scrollToBottom);
        }
      },
      clearBubbleHtml: () => {
        localStreamingManagerRef.current.updateBubbleHtml(bubbleId, "");
      },
      clearToolUi: () => {
        localStreamingManagerRef.current.updateToolUiHtml(bubbleId, "");
      },
      updateBubbleDurationText: (text) => {
        localStreamingManagerRef.current.updateBubbleDurationText(bubbleId, text);
      },
      scrollThinkingBodyToBottom: () =>
        localStreamingManagerRef.current.scrollThinkingBodyToBottom(bubbleId),
    }),
    [scrollToBottom]
  );

  const invokeBubbleHook = useCallback(
    (bubble, hookName) => {
      if (!bubble?.bubbleId) {
        return;
      }
      const hook = bubble[hookName];
      if (typeof hook !== "function") {
        return;
      }
      hook(bubble, buildBubbleUiApi(bubble.bubbleId));
    },
    [buildBubbleUiApi]
  );

  const updateBubble = useCallback(
    (bubbleId, updates) => {
      let updatedBubble = null;
      let previousBubble = null;

      setBubbles((existingBubbles) =>
        existingBubbles.map((bubble) => {
          if (bubble.bubbleId !== bubbleId) {
            return bubble;
          }

          previousBubble = bubble;
          updatedBubble =
            typeof bubble.update === "function"
              ? bubble.update(bubble, updates)
              : { ...bubble, ...updates };

          if (typeof updatedBubble.html !== "string") {
            updatedBubble.html =
              typeof updatedBubble.toolUiHtml === "string"
                ? updatedBubble.toolUiHtml
                : "";
          }

          return updatedBubble;
        })
      );

      if (!updatedBubble) {
        return;
      }

      if (typeof updatedBubble.html === "string") {
        localStreamingManagerRef.current.updateBubbleHtml(
          bubbleId,
          updatedBubble.html,
          updatedBubble.content
        );
      }

      if (updatedBubble.isComplete && !previousBubble?.isComplete) {
        invokeBubbleHook(updatedBubble, "onBubbleCompleted");
      } else {
        invokeBubbleHook(updatedBubble, "onBubbleUpdated");
      }
    },
    [invokeBubbleHook]
  );

  const handleImageClick = useCallback(
    (bubbleId, imageIndex, allImages) => {
      onImageClick?.(bubbleId, imageIndex, allImages);
    },
    [onImageClick]
  );

  const handleFileClick = useCallback(
    (bubbleId, fileIndex, allFiles) => {
      onFileClick?.(bubbleId, fileIndex, allFiles);
    },
    [onFileClick]
  );

  const handleOpenFileByPath = useCallback(
    (path) => {
      onOpenFileByPath?.(path);
    },
    [onOpenFileByPath]
  );

  const handleBubbleClickAction = useCallback(
    (action) => {
      onBubbleClickAction?.(action);
    },
    [onBubbleClickAction]
  );

  const onOpenUrlRef = useRef(onOpenUrl);
  onOpenUrlRef.current = onOpenUrl;
  const handleOpenUrl = useCallback((url) => {
    onOpenUrlRef.current?.(url);
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const currentScrollTop = container.scrollTop;
    const lastScrollTop = lastScrollTopRef.current;
    const delta = currentScrollTop - lastScrollTop;
    lastScrollTopRef.current = currentScrollTop;

    const nearBottom = getIsNearBottom();
    setIsNearBottom(nearBottom);

    if (isAutoAdjustingRef.current) {
      return;
    }

    if (delta < 0) {
      setIsAutoScrollEnabled(false);
      autoScrollEnabledRef.current = false;
      userScrollCooldownUntilRef.current = Date.now() + 400;
      return;
    }

    if (isAutoScrollEnabled && !nearBottom) {
      setIsAutoScrollEnabled(false);
    }
  }, [getIsNearBottom, isAutoScrollEnabled]);

  const handleScrollToBottom = useCallback(() => {
    setIsAutoScrollEnabled(true);
    autoScrollEnabledRef.current = true;
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    lastScrollTopRef.current = container.scrollTop;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const container = contentContainerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(() => {
      if (autoScrollEnabledRef.current) {
        scrollToBottom();
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  useEffect(() => {
    if (autoScrollEnabledRef.current) {
      scrollToBottom();
    }
  }, [bubbles, scrollToBottom]);

  useImperativeHandle(
    ref,
    () => ({
      getBubbles: () => bubblesRef.current,
      streamingManager: localStreamingManagerRef.current,
      handleImageClick,
      handleFileClick,
      createBubble: (preset, props = {}) => {
        const bubbleConfig =
          typeof preset === "string"
            ? buildLegacyBubbleConfig(preset, props)
            : { ...preset, ...props };

        if (!bubbleConfig) {
          return null;
        }

        const newBubble = {
          bubbleId: bubbleConfig.bubbleId || createBubbleId(),
          messageId:
            bubbleConfig.messageId ?? props.messageId ?? createMessageId(),
          ...bubbleConfig,
        };

        if (typeof newBubble.getContent !== "function") {
          newBubble.getContent = (bubble) => bubble?.content || "";
        }
        if (typeof newBubble.html !== "string") {
          newBubble.html =
            typeof newBubble.toolUiHtml === "string" ? newBubble.toolUiHtml : "";
        }

        setBubbles((existingBubbles) => [...existingBubbles, newBubble]);
        localStreamingManagerRef.current.updateBubbleHtml(
          newBubble.bubbleId,
          newBubble.html,
          newBubble.content
        );
        invokeBubbleHook(newBubble, "onBubbleInit");
        return newBubble.bubbleId;
      },
      getBubbleContent: (bubbleId) => {
        const bubble = bubblesRef.current.find((entry) => entry.bubbleId === bubbleId);
        if (bubble && typeof bubble.getContent === "function") {
          return bubble.getContent(bubble);
        }
        return bubble?.content || "";
      },
      removeBubble: (bubbleId) => {
        const bubble = bubblesRef.current.find((entry) => entry.bubbleId === bubbleId);
        if (bubble) {
          invokeBubbleHook(bubble, "onBubbleRemoved");
        }
        localStreamingManagerRef.current.bubbleHtmlByBubbleId.delete(bubbleId);
        localStreamingManagerRef.current.elements.delete(bubbleId);
        setBubbles((existingBubbles) =>
          existingBubbles.filter((bubbleEntry) => bubbleEntry.bubbleId !== bubbleId)
        );
      },
      clearBubbles: () => {
        bubblesRef.current.forEach((bubble) => {
          invokeBubbleHook(bubble, "onBubbleRemoved");
        });
        localStreamingManagerRef.current.elements.clear();
        localStreamingManagerRef.current.bubbleHtmlByBubbleId.clear();
        setBubbles([]);
      },
      scrollToBottomAndEnable: () => {
        setIsAutoScrollEnabled(true);
        autoScrollEnabledRef.current = true;
        scrollToBottom();
      },
      updateBubble,
      updateBubbleHtml: (bubbleId, html, rawContent) => {
        localStreamingManagerRef.current.updateBubbleHtml(
          bubbleId,
          html,
          rawContent
        );
      },
      updateToolUiHtml: (bubbleId, html) => {
        localStreamingManagerRef.current.updateToolUiHtml(bubbleId, html);
      },
      updateBubbleDurationText: (bubbleId, text) => {
        localStreamingManagerRef.current.updateBubbleDurationText(bubbleId, text);
      },
    }),
    [
      handleImageClick,
      handleFileClick,
      invokeBubbleHook,
      scrollToBottom,
      updateBubble,
    ]
  );

  const streamingMgr = localStreamingManagerRef.current;

  const chatAreaMethods = useMemo(
    () => ({
      handleImageClick,
      handleFileClick,
      handleOpenFileByPath,
      handleOpenUrl,
      handleBubbleClickAction,
      updateBubble,
    }),
    [
      handleImageClick,
      handleFileClick,
      handleOpenFileByPath,
      handleOpenUrl,
      handleBubbleClickAction,
      updateBubble,
    ]
  );

  const showScrollButton = !isAutoScrollEnabled && !isNearBottom;

  return (
    <div className="chat-area-container">
      <AIToolUiStyleHost aiToolSystem={aiToolSystem} />
      <div className="ai-chat-area" ref={scrollContainerRef}>
        <div className="ai-chat-area-content" ref={contentContainerRef}>
          {bubbles.map((bubble) => (
            <ChatBubble
              key={bubble.bubbleId}
              {...bubble}
              streamingManager={streamingMgr}
              chatAreaMethods={chatAreaMethods}
              onContextMenu={(event) => onBubbleContextMenu?.(event, bubble)}
              isMutationBlocked={isMutationBlocked}
            />
          ))}
          <div className="ai-chat-area-spacer" />
        </div>
      </div>

      {showScrollButton && (
        <button
          className="scroll-to-bottom-btn"
          onClick={handleScrollToBottom}
          title="Scroll to bottom and enable auto-scroll"
        >
          <i className="fa-solid fa-chevron-down"></i>
        </button>
      )}
    </div>
  );
});

export default ChatArea;
export { ChatArea };
